import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/view/components/ui/dialog'
import { Button } from '@/view/components/ui/button'
import { RectangleEllipsis } from 'lucide-react'
import { useToast } from '@/view/components/ui/use-toast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CurrentUserResponse } from '@/app/services/usersService/user'
import { UserPinParams } from '@/app/services/usersService/userPin'
import { usersService } from '@/app/services/usersService'
import { PinCreateOrUpdate } from './components/PinCreateOrUpdate'
import { DeletePinForm } from './components/DeletePinForm'
import { SellerInfo } from '@/app/entities/SellerInfo'
import { useAuth } from '@/app/hooks/useAuth'
import { useParams } from 'react-router-dom'

type ModalState = 'initial' | 'update' | 'delete'

interface UserPinProps {
  user?: CurrentUserResponse | SellerInfo
}

export function UserPin({ user }: UserPinProps) {
  const [open, setOpen] = useState(false)
  const [modalState, setModalState] = useState<ModalState>('initial')
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { isAdmin, isSupport } = useAuth()
  const { id } = useParams()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (data: UserPinParams) => {
      return isAdmin || isSupport
        ? usersService.userAdminPinUpdate(data, id)
        : usersService.userPin(data)
    },
    onSuccess: () => {
      setOpen(false)
      setModalState('initial')
      queryClient.invalidateQueries({
        queryKey: ['users', 'currentUser'],
      })
    },
    onError: (error) => {
      console.error('error', error)
      toast({
        variant: 'destructive',
        title:
          error?.message ||
          'Erro ao registrar o pin, tente novamente mais tarde.',
      })
    },
  })

  const handleSubmit = async (data: {
    pin: string[]
    oldPassword?: string
  }) => {
    await mutateAsync({
      oldPassword: data.oldPassword,
      pin: modalState === 'delete' ? 'remove' : data.pin.join(''),
    })
  }

  const handleUpdateOrDelete = (action: 'update' | 'delete') => {
    setModalState(action)
  }

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) {
      setModalState('initial')
    }
  }

  const renderDialogContent = () => {
    if (!user?.isPinProtected || modalState === 'initial') {
      return (
        <>
          <DialogHeader>
            <DialogTitle className="max-w-80 text-center text-xl font-semibold">
              {user?.isPinProtected
                ? 'Você quer alterar ou apagar o seu pin?'
                : 'Cadastrar PIN'}
            </DialogTitle>
          </DialogHeader>
          {user?.isPinProtected ? (
            <div className="mt-4 flex justify-end gap-2">
              <Button
                onClick={() => handleUpdateOrDelete('delete')}
                variant="outline"
                className="border-white04 border-[1px] bg-transparent text-primary hover:bg-transparent"
              >
                Apagar
              </Button>

              <Button
                onClick={() => handleUpdateOrDelete('update')}
                className="text-white"
              >
                Alterar
              </Button>
            </div>
          ) : (
            <PinCreateOrUpdate isPending={isPending} onSubmit={handleSubmit} />
          )}
        </>
      )
    }

    if (modalState === 'update') {
      return <PinCreateOrUpdate isPending={isPending} onSubmit={handleSubmit} />
    }

    if (modalState === 'delete') {
      return <DeletePinForm isPending={isPending} onSubmit={handleSubmit} />
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <div className="flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-sm border-[1px]">
          <RectangleEllipsis size="20" />
          <p>{user?.isPinProtected ? 'Alterar PIN' : 'Cadastrar PIN'}</p>
        </div>
      </DialogTrigger>
      <DialogContent className="w-full p-5 sm:w-96">
        {renderDialogContent()}
      </DialogContent>
    </Dialog>
  )
}
